import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Swords, Scale, Flame, Sun, Telescope, Shield, Gavel, Laugh, Play, Newspaper, Headphones } from "lucide-react";
import { TeaserDuel } from "@/components/TeaserDuel";
import { ComingSoonTeaser } from "@/components/ComingSoonTeaser";
import { CinematicVideo } from "@/components/CinematicVideo";

const LABO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/r85po8jk_gemini_generated_video_d4555f6e.mp4";

const PROCES_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/07f7cbefa733e3fc9bd8edcf18b45bc03de126dacede3264e2d97aabc57b9937.jpeg";
const DOUTE_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/51829eef3b80f9aae447a5a922d977ec0ed96f47bea9582f5ea0a768291bddd2.jpeg";
const TROIS_AGES_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/2c76db4dcb7f5b0810f20322b99a5a666a85d89808bc6397513c83ebe4968e59.jpeg";
const EXPEDITION_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/daf248c438b326d07a732be8fd369228b0f91c34222c2085694b32b82c5d3a80.jpeg";
const PROCES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/eib6e99b_gemini_generated_video_d4cfc4e2.mp4";
const DOUTE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zhn93q78_Le_Feu_du_Doute_Bouddha_prend.mp4";
const TROIS_AGES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/sub4i57n_gemini_generated_video_94d9e890.mp4";
const EXPEDITION_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/cr5q9c27_gemini_generated_video_31c1a5ab.mp4";

const PROCES_PERSONAS = [
  { Icon: Swords, color: "red" },
  { Icon: Shield, color: "teal" },
  { Icon: Gavel, color: "gold" },
];
const DOUTE_PERSONAS = [{ Icon: Flame, color: "red" }];
const VERITES_PERSONAS = [
  { Icon: Laugh, color: "grey" },
  { Icon: Swords, color: "red" },
  { Icon: Sun, color: "gold" },
];
const EXPEDITION_PERSONAS = [
  { Icon: Play, color: "gold" },
  { Icon: Newspaper, color: "teal" },
  { Icon: Headphones, color: "red" },
];

export function LaboHub() {
  const { t } = useTranslation();
  const [active, setActive] = useState("duel");

  const items = [
    { key: "duel", Icon: Swords, node: <TeaserDuel /> },
    { key: "proces", Icon: Scale, node: <ComingSoonTeaser ns="proces" Icon={Scale} image={PROCES_IMG} video={PROCES_VIDEO} personas={PROCES_PERSONAS} testId="proces-teaser" navTab="proces" /> },
    { key: "doute", Icon: Flame, node: <ComingSoonTeaser ns="doute" Icon={Flame} image={DOUTE_IMG} video={DOUTE_VIDEO} personas={DOUTE_PERSONAS} testId="doute-teaser" navTab="doute" /> },
    { key: "verites", Icon: Sun, node: <ComingSoonTeaser ns="verites" Icon={Sun} image={TROIS_AGES_IMG} video={TROIS_AGES_VIDEO} personas={VERITES_PERSONAS} testId="verites-teaser" navTab="verites" /> },
    { key: "expedition", Icon: Telescope, node: <ComingSoonTeaser ns="expedition" Icon={Telescope} image={EXPEDITION_IMG} video={EXPEDITION_VIDEO} personas={EXPEDITION_PERSONAS} testId="expedition-teaser" navTab="expedition" /> },
  ];

  const activeItem = items.find((i) => i.key === active);

  return (
    <div className="w-full pt-6" data-testid="labo-hub">
      <div className="max-w-6xl mx-auto px-6">
        <p className="text-primary/80 text-[11px] uppercase tracking-[0.3em] mb-4 text-center">{t("landing.labo.eyebrow")}</p>
        <h3 className="font-display text-gradient-orange text-3xl sm:text-4xl leading-tight mb-3 pb-[0.06em] text-center">{t("landing.labo.title")}</h3>
        <p className="text-foreground/60 text-sm max-w-xl mx-auto mb-7 leading-relaxed text-center">{t("landing.labo.hint")}</p>

        <div className="max-w-2xl mx-auto mb-8">
          <CinematicVideo fade noSound silver src={LABO_VIDEO} testId="labo-cinematic-video" caption={t("landing.labo.title")} />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-2 mb-1" data-testid="labo-tabs">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2" role="tablist">
            {items.map((it) => {
              const on = active === it.key;
              const Icon = it.Icon;
              return (
                <button
                  key={it.key}
                  onClick={() => setActive(it.key)}
                  role="tab"
                  aria-selected={on}
                  data-testid={`labo-tab-${it.key}`}
                  className={`group flex flex-col items-center justify-center text-center gap-2 rounded-xl px-2.5 py-4 min-h-[76px] border transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
                    on
                      ? "border-primary/50 bg-primary/[0.12] text-primary shadow-[0_0_22px_-6px_hsl(28_96%_56%/0.55)]"
                      : "border-white/8 text-foreground/55 hover:text-primary hover:border-primary/25 hover:bg-white/[0.03]"
                  }`}
                >
                  <Icon size={18} className={on ? "" : "opacity-70 group-hover:opacity-100 transition-opacity"} />
                  <span className="text-[13px] leading-tight font-medium">{t(`landing.${it.key}.name`)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`labo-panel-${active}`}
        >
          {activeItem?.node}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
